import { Helmet } from 'react-helmet';

const cleanupPrerenderedHead = () => {
  if (typeof window === 'undefined' || window.location.pathname === '/pricing') return;

  const selectors = [
    'meta[name="description"]',
    'link[rel="canonical"]',
    ...['og:title', 'og:description', 'og:url', 'og:type', 'og:image', 'og:image:alt']
      .map((key) => `meta[property="${key}"]`),
    ...['twitter:title', 'twitter:description', 'twitter:url', 'twitter:image', 'twitter:image:alt']
      .map((key) => `meta[name="${key}"]`),
  ];
  for (const selector of selectors) {
    if (!document.head.querySelector(`${selector}[data-react-helmet="true"]`)) continue;
    document.head.querySelectorAll(`${selector}:not([data-react-helmet="true"])`)
      .forEach((tag) => tag.remove());
  }
};

interface SEOHeadProps {
  title: string;
  description: string;
  canonical?: string;
  ogImage?: string;
  noindex?: boolean;
  type?: 'website' | 'article';
  jsonLd?: object;
  /** Titles get " | Vovy" appended by default. Pass false when the title
   *  already carries the brand, so it doesn't read "… | Vovy | Vovy". */
  appendSiteName?: boolean;
}

const SEOHead = ({
  title,
  description,
  canonical,
  ogImage = 'https://www.vovy.ai/og-image.png',
  noindex = false,
  type = 'website',
  jsonLd,
  appendSiteName = true
}: SEOHeadProps) => {
  const siteName = 'Vovy';
  const twitterHandle = '@vovy_ai';
  const fullTitle = appendSiteName ? `${title} | ${siteName}` : title;
  const currentUrl = canonical || `https://www.vovy.ai${typeof window !== 'undefined' ? window.location.pathname : ''}`;
  
  return (
    <Helmet onChangeClientState={cleanupPrerenderedHead}>
      {/* Basic Meta Tags */}
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      <meta name="author" content={siteName} />
      
      {/* Canonical URL */}
      <link rel="canonical" href={currentUrl} />
      
      {/* Robots */}
      {noindex && <meta name="robots" content="noindex, nofollow" />}
      {!noindex && <meta name="robots" content="index, follow" />}
      <meta name="googlebot" content={noindex ? 'noindex, nofollow' : 'index, follow'} />
      
      {/* Open Graph / Facebook */}
      <meta property="og:type" content={type} />
      <meta property="og:site_name" content={siteName} />
      <meta property="og:url" content={currentUrl} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:image:alt" content={title} />
      <meta property="og:locale" content="en_US" />
      
      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:site" content={twitterHandle} />
      <meta name="twitter:creator" content={twitterHandle} />
      <meta name="twitter:url" content={currentUrl} />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={ogImage} />
      <meta name="twitter:image:alt" content={title} />
      
      {/* Additional SEO tags */}
      <meta name="language" content="English" />
      <meta httpEquiv="content-language" content="en-US" />
      
      {/* Structured Data (JSON-LD) */}
      {jsonLd && (
        <script type="application/ld+json">
          {JSON.stringify(jsonLd)}
        </script>
      )}
    </Helmet>
  );
};

export default SEOHead;
